// ================================================================
// BabyOdin 活动系统API客户端工具
// 专用于活动相关API的签名验证和请求处理
// 版本: 1.0
// 创建日期: 2025-09-02
// ================================================================

import type { DelegationIdentity } from "@icp-sdk/core/identity";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("lib/utils/activity-api-client.ts");

/**
 * 为活动系统API调用构建请求头
 * 包含签名验证所需的所有头信息
 */
export async function buildActivityApiHeaders(
	identity: DelegationIdentity,
	principal: string,
	method: string,
	path: string,
	body?: any,
): Promise<Record<string, string>> {
	try {
		const timestamp = Date.now().toString();

		// 构造签名消息
		let message: string;
		if (method === "POST" || method === "PUT") {
			// 对于有请求体的操作，消息格式为：方法:时间戳:请求体
			const bodyString = body ? JSON.stringify(body) : "";
			message = `${method}:${timestamp}:${bodyString}`;
		} else {
			// 对于GET和DELETE，消息格式为：方法:时间戳:路径
			message = `${method}:${timestamp}:${path}`;
		}

		// 签名消息
		const messageBytes = new TextEncoder().encode(message);
		const signature = await identity.sign(messageBytes);

		// 获取委托数据
		const delegation = identity.getDelegation();
		const delegationJson = JSON.stringify(delegation.toJSON());

		// 构建请求头
		const headers: Record<string, string> = {
			"Content-Type": "application/json",
			"X-User-Principal": principal,
			"X-Timestamp": timestamp,
			"X-Signature": Buffer.from(signature).toString("base64"),
			"X-Delegation": btoa(delegationJson),
		};

		uiLog.debug("🔗 构建活动API请求头:", {
			method,
			path,
			principal: principal.substring(0, 20) + "...",
			timestamp,
			signatureLength: signature.byteLength,
			delegationLength: delegationJson.length,
		});

		return headers;
	} catch (error) {
		uiLog.error("❌ 构建活动API请求头失败:", error);
		throw new Error(
			"无法构建API请求头：" +
				(error instanceof Error ? error.message : "未知错误"),
		);
	}
}

/**
 * 活动系统专用的API调用函数
 * 自动处理签名验证和错误处理
 */
export async function callActivityApi<T = any>(
	identity: DelegationIdentity,
	principal: string,
	method: "GET" | "POST" | "PUT" | "DELETE",
	endpoint: string,
	body?: any,
): Promise<{ success: boolean; data?: T; error?: string }> {
	try {
		const url = new URL(endpoint, window.location.origin);
		const headers = await buildActivityApiHeaders(
			identity,
			principal,
			method,
			url.pathname,
			body,
		);

		const requestOptions: RequestInit = {
			method,
			headers,
		};

		if (body && (method === "POST" || method === "PUT")) {
			requestOptions.body = JSON.stringify(body);
		}

		uiLog.info(`🚀 调用活动API: ${method} ${endpoint}`);

		const response = await fetch(endpoint, requestOptions);
		const result = await response.json();

		if (response.ok && result.success) {
			uiLog.info(`✅ 活动API调用成功: ${method} ${endpoint}`);
			return result;
		} else {
			uiLog.error(`❌ 活动API调用失败: ${method} ${endpoint}`, {
				status: response.status,
				error: result.error,
			});
			return {
				success: false,
				error: result.error || `API调用失败 (${response.status})`,
			};
		}
	} catch (error) {
		uiLog.error(`❌ 活动API调用异常: ${method} ${endpoint}`, error);
		return {
			success: false,
			error: error instanceof Error ? error.message : "网络请求异常",
		};
	}
}

/**
 * 活动账号管理专用的API调用函数
 * 继承自 callActivityApi，但增加了特定的错误处理
 */
export async function callActivityAccountApi<T = any>(
	identity: DelegationIdentity,
	principal: string,
	method: "GET" | "POST" | "PUT" | "DELETE",
	endpoint: string,
	body?: any,
): Promise<{ success: boolean; data?: T; error?: string }> {
	const result = await callActivityApi<T>(
		identity,
		principal,
		method,
		endpoint,
		body,
	);

	// 活动账号特定的错误处理
	if (!result.success && result.error) {
		if (
			result.error.includes("权限不足") ||
			result.error.includes("超级账号权限")
		) {
			result.error = "需要超级账号权限才能执行此操作";
		} else if (result.error.includes("委托已过期")) {
			result.error = "登录会话已过期，请重新登录";
		} else if (result.error.includes("签名验证失败")) {
			result.error = "身份验证失败，请重新登录";
		}
	}

	return result;
}

/**
 * 兼容性函数：将旧的API调用方式转换为新的验证机制
 * 可以逐步替换现有的API调用代码
 */
export function createActivityApiClient(
	identity: DelegationIdentity,
	principal: string,
) {
	return {
		// 活动管理
		createActivity: (data: any) =>
			callActivityApi(
				identity,
				principal,
				"POST",
				"/api/activities/create",
				data,
			),
		updateActivity: (id: string, data: any) =>
			callActivityApi(
				identity,
				principal,
				"PUT",
				`/api/activities/${id}`,
				data,
			),
		deleteActivity: (id: string) =>
			callActivityApi(identity, principal, "DELETE", `/api/activities/${id}`),
		joinActivity: (id: string) =>
			callActivityApi(
				identity,
				principal,
				"POST",
				`/api/activities/${id}/join`,
			),
		startActivity: (id: string) =>
			callActivityApi(
				identity,
				principal,
				"POST",
				`/api/activities/${id}/start`,
			),
		endActivity: (id: string) =>
			callActivityApi(identity, principal, "POST", `/api/activities/${id}/end`),

		// 活动账号管理
		getActivityAccounts: () =>
			callActivityAccountApi(
				identity,
				principal,
				"GET",
				"/api/activity-accounts",
			),
		createActivityAccount: (data: any) =>
			callActivityAccountApi(
				identity,
				principal,
				"POST",
				"/api/activity-accounts",
				data,
			),
		deleteActivityAccount: (targetPrincipal: string) =>
			callActivityAccountApi(
				identity,
				principal,
				"DELETE",
				`/api/activity-accounts/${targetPrincipal}`,
			),
		getActivityAccountPermission: (targetPrincipal: string) =>
			callActivityAccountApi(
				identity,
				principal,
				"GET",
				`/api/activity-accounts/${targetPrincipal}`,
			),
	};
}
